/**
 * Presentational range visuals for the Central ⇄ Range feature.
 *
 *   <RangeWhisker> — a compact Floor · Central · Ceiling bracket rendered
 *   under a hero/KPI number. Ends are postures (Floor / Ceiling), colored
 *   benefit (teal/green) vs harm (amber), never bare low/high.
 *
 *   <RangeBar> — a horizontal midpoint bar (central length) overlaid with a
 *   translucent floor→ceiling band and Floor/Ceiling caps, matching the
 *   mockup's breakdown rows.
 *
 * Both are pure/stateless — callers gate rendering on
 * `scenarios.status === 'complete'` and range mode.
 */

const BENEFIT = "#26bf72"; // quantum-400
const HARM = "#f59e0b"; // health-500 (amber)

function pct(v: number, lo: number, hi: number): number {
  if (hi === lo) return 50;
  return ((v - lo) / (hi - lo)) * 100;
}

interface RangeWhiskerProps {
  floor: number;
  central: number;
  ceiling: number;
  /** teal when true (avoided/benefit), amber when false (harm/burden). */
  benefit: boolean;
  format: (n: number) => string;
  className?: string;
}

export function RangeWhisker({
  floor,
  central,
  ceiling,
  benefit,
  format,
  className = "",
}: RangeWhiskerProps) {
  const lo = Math.min(floor, central, ceiling);
  const hi = Math.max(floor, central, ceiling);
  const color = benefit ? BENEFIT : HARM;
  const softBg = `${color}22`;
  const fillBg = `${color}80`;

  const fPct = pct(floor, lo, hi);
  const cPct = pct(central, lo, hi);
  const hPct = pct(ceiling, lo, hi);
  const bandLeft = Math.min(fPct, hPct);
  const bandWidth = Math.abs(hPct - fPct);

  return (
    <div className={`mt-3 ${className}`}>
      <div
        className="relative h-1.5 rounded"
        style={{ background: softBg }}
        role="img"
        aria-label={`Sensitivity range from ${format(floor)} to ${format(ceiling)}, central ${format(central)}`}
      >
        {/* floor→ceiling fill */}
        <span
          className="absolute inset-y-0 rounded"
          style={{ left: `${bandLeft}%`, width: `${bandWidth}%`, background: fillBg }}
        />
        {/* end caps */}
        <span
          className="absolute w-0.5 rounded"
          style={{ left: `${fPct}%`, top: "-3px", height: "12px", background: "var(--tw-cap, #94a3b8)", backgroundColor: "#94a3b8" }}
        />
        <span
          className="absolute w-0.5 rounded"
          style={{ left: `${hPct}%`, top: "-3px", height: "12px", backgroundColor: "#94a3b8" }}
        />
        {/* central node */}
        <span
          className="absolute rounded-full"
          style={{
            left: `${cPct}%`,
            top: "50%",
            width: "10px",
            height: "10px",
            transform: "translate(-50%,-50%)",
            background: color,
            border: "2px solid var(--surface-node, #0b1220)",
            boxShadow: "0 0 0 1px rgba(0,0,0,0.15)",
          }}
        />
      </div>
      <div className="mt-1.5 flex justify-between text-[10px] tabular-nums text-text-muted">
        <span className="text-left">
          <span className="block text-[8px] font-semibold uppercase tracking-wide text-text-muted">
            Floor
          </span>
          {format(floor)}
        </span>
        <span className="text-right">
          <span className="block text-[8px] font-semibold uppercase tracking-wide text-text-muted">
            Ceiling
          </span>
          {format(ceiling)}
        </span>
      </div>
    </div>
  );
}

interface RangeBarProps {
  label: string;
  floor: number;
  central: number;
  ceiling: number;
  /** scale denominator (max |ceiling| across rows) */
  max: number;
  color: string;
  format: (n: number) => string;
}

export function RangeBar({ label, floor, central, ceiling, max, color, format }: RangeBarProps) {
  const denom = max > 0 ? max : 1;
  const w = (v: number) => `${Math.min(100, (Math.abs(v) / denom) * 100)}%`;
  const loW = Math.min(Math.abs(floor), Math.abs(ceiling));
  const hiW = Math.max(Math.abs(floor), Math.abs(ceiling));

  return (
    <div className="grid grid-cols-[140px_1fr_auto] items-center gap-3 border-t border-border/60 py-1.5 first:border-t-0">
      <span className="truncate text-[12px] text-text-secondary" title={label}>
        {label}
      </span>
      <span className="relative h-5">
        {/* floor→ceiling band */}
        <span
          className="absolute rounded-sm"
          style={{
            top: 0,
            height: "20px",
            left: w(loW),
            width: `calc(${w(hiW)} - ${w(loW)})`,
            background: color,
            opacity: 0.16,
          }}
        />
        {/* central bar */}
        <span
          className="absolute rounded-sm"
          style={{ top: "4px", height: "12px", left: 0, width: w(central), background: color, opacity: 0.9 }}
        />
        {/* caps */}
        <span className="absolute w-0.5" style={{ top: "2px", height: "16px", left: w(loW), background: color, opacity: 0.6 }} />
        <span className="absolute w-0.5" style={{ top: "2px", height: "16px", left: w(hiW), background: color, opacity: 0.6 }} />
      </span>
      <span className="min-w-[92px] text-right font-mono text-[12px] tabular-nums text-text-primary">
        {format(central)}
        <span className="block text-[10px] text-text-muted">
          {format(floor)}–{format(ceiling)}
        </span>
      </span>
    </div>
  );
}
